<script setup>
import {ref, onMounted, computed, onUnmounted} from "vue";
import MenuMobile from "./MenuMobile.vue";
import SliderMenuProducts from "./sliderMenuProducts.vue";
import SliderMenuUrbano from "./sliderMenuUrbano.vue";

const props = defineProps({
  locale: {type: String, required: true, default: "pt"},
  model: {type: Object, required: true},
  color: {
    type: String,
    default: 'orange'
  }
});

const MenuData = ref([]);
const isMenuOpen = ref(false);
const query = ref("");
const activeMenu = ref(null);
const isMobileMenuOpen = ref(false);
const inputSearchFocus = ref(null);
const searchHistory = ref([]);
const searchAction = computed(() => {
  return `/${props.locale}/search`;
});

const currentColor = computed(() => {
  const currentPath = window.location.pathname.toLowerCase();
  if (currentPath.includes('mobilidade-rural')) {
    return 'orange';
  } else if (currentPath.includes('mobilidade-urbana')) {
    return 'blue';
  }
  return props.color;
});

const colorClasses = computed(() => {
  return {
    'orange': currentColor.value === 'orange',
    'blue': currentColor.value === 'blue'
  };
});
const translations = {
  pt: "O que procura?"
};

const toggleMenuMobile = () => {
  isMenuOpen.value = !isMenuOpen.value
}
const closeMobileMenu = (e) => {
  if (!isMobileMenuOpen.value) return;

  const menu = document.querySelector('.sidebar-menu');
  const btn = document.querySelector('.hamburger-button');
  if (menu && !menu.contains(e.target) && btn && !btn.contains(e.target)) {
    isMobileMenuOpen.value = false;
  }
};

const closeMenu = () => {
  isMenuOpen.value = false
}

function isProductMenu(item) {
  return item.label.toLowerCase().includes('produto');
}

function hasChildren(item) {
  return (item.childs && item.childs.length > 0) || isProductMenu(item);
}

const searchPlaceholder = computed(() => translations[props.locale] || translations.en);

function toggleMenu(menuType) {
  activeMenu.value = activeMenu.value === menuType ? null : menuType;
  if (activeMenu.value === 'search') {
    setTimeout(() => inputSearchFocus.value?.focus(), 400);
  }
}

function toggleMobileMenu() {
  isMobileMenuOpen.value = !isMobileMenuOpen.value;
  if (isMobileMenuOpen.value) {
    activeMenu.value = null;
    document.body.style.overflow = 'hidden';
  } else {
    document.body.style.overflow = '';
  }
}

const fetchMenuData = async () => {
  try {
    const response = await fetch("/api/getmenu");
    MenuData.value = await response.json();
  } catch (e) {
    console.error("Erro ao carregar menu", e);
  }
};

const handleClickOutside = (event) => {
  if (!event.target.closest('.header-wrapper')) {
    activeMenu.value = null;
    if (isMobileMenuOpen.value) toggleMobileMenu();
  }
};

onMounted(() => {
  document.addEventListener("mousedown", handleClickOutside);
  const saved = localStorage.getItem('search_history');
  if (saved) searchHistory.value = JSON.parse(saved);
  window.addEventListener('touchstart', closeMobileMenu, { passive: true });
  window.addEventListener('mousedown', closeMobileMenu);
  fetchMenuData();
});

onUnmounted(() => {
  document.removeEventListener("mousedown", handleClickOutside);
  document.body.style.overflow = '';
  window.removeEventListener('touchstart', closeMobileMenu);
  window.removeEventListener('mousedown', closeMobileMenu);
});


function handleSearch() {
  const searchTerm = query.value.trim();
  if (!searchTerm) return;

  const newHistory = [
    searchTerm,
    ...searchHistory.value.filter(item => item !== searchTerm)
  ].slice(0, 5);

  searchHistory.value = newHistory;
  localStorage.setItem('search_history', JSON.stringify(newHistory));
  isMenuOpen.value = false;
  window.location.href = `${searchAction.value}?q=${encodeURIComponent(searchTerm)}`;
}
</script>

<template>
  <header class="header-wrapper">
    <nav class="navbar nav-menu container-nav">
      <div class="nav-container">
        <div class="logo">
          <a href="/">
            <svg xmlns="http://www.w3.org/2000/svg" width="137" height="55" viewBox="0 0 137 55" fill="none">
              <path
                d="M83.9111 20.499L76.8068 36.8174L78.5414 40.8884C79.3361 40.4635 79.9443 39.7919 80.3797
                38.8049L88.3755 20.499H83.9111Z"
                fill="white"/>
              <path
                d="M75.2104 37.537C74.934 37.4479 74.7336 37.2766 74.623 36.9819L67.726 20.499H63.3376L70.9395
                38.8049C71.7205 40.7102 73.2547 41.4847 75.6458 41.4847C76.0812 41.4847 76.4889 41.4573 76.869
                41.4025L75.2173 37.5233L75.2104 37.537Z"
                fill="white"/>
              <path
                d="M93.6277 20.4991V15.6399H97.8225V20.4991H103.545V24.4673H97.8225V34.1857C97.8225 36.4816
                98.6449 37.2561 100.601 37.2561H103.462V41.0256H99.7368C95.466 41.0256 93.6208 39.3122 93.6208
                33.9527V24.4673H90.8011V20.4991H93.6208H93.6277Z"
                fill="white"/>
              <path
                d="M52.7917 20.499H48.4448C42.9161 20.499 39.4677 23.8436 39.4677 30.7588C39.4677 37.6741 42.9161
                41.0186 48.4448 41.0186H52.7917C58.2789 41.0186 61.7689 37.6741 61.7689 30.7588C61.7689 23.8436
                58.2789 20.499 52.7917 20.499ZM52.3978 37.023H48.8318C45.2244 37.023 43.6556 35.1931 43.6556
                30.7657C43.6556 26.3383 45.2244 24.4672 48.8318 24.4672H52.3978C55.9638 24.4672 57.574 26.3314 57.574
                30.7657C57.574 35.2 55.9707 37.023 52.3978 37.023Z"
                fill="white"/>
              <path
                d="M24.1809 20.499C31.1954 20.499 34.6439 23.8024 34.6439 29.4772V41.0186H30.4491V29.4772C30.4491
                26.2492 28.5693 24.4672 24.2155 24.4672H19.3572V41.0255H15.1623V24.4672H4.6648C4.31235 24.4672
                4.1534 24.6591 4.1534 25.0498V40.8884H0V23.6859C0 21.2735 0.822387 20.499 2.90254
                20.499H24.1879H24.1809Z"
                fill="white"/>
              <path
                d="M80.4834 15.2012C85.4799 11.2193 91.6582 8.86168 98.334 8.86168C105.01 8.86168 111.181 11.2193
                116.185 15.2012H128.037C121.008 5.92835 110.317 0 98.334 0C86.3507 0 75.6596 5.92835 68.6313
                15.2012H80.4834Z"
                fill="#EE7623"/>
              <path d="M120.255 37.0231L121.962 41.0256H127.021V37.0231H120.255Z" fill="#A7CB47"/>
              <path
                d="M111.188 27.3046C111.188 25.7077 112.087 24.4672 114.36 24.4672H121.022C123.061 24.4672 123.959
                25.4747 123.959 27.0305C123.959 28.5862 123.137 29.4772 120.905 29.4772H111.188V27.2978V27.3046ZM114.36
                20.499 109.184 20.499 107.152 23.3775 107.152 27.2635V34.261C107.152 38.147 109.191 41.0255 114.36
                41.0255H120.055L118.348 37.023H114.36C112.087 37.023 111.188 35.8168 111.188
                34.1856V32.7053H121.022C126.115 32.7053 128.037 30.5669 128.037 26.7975C128.037 22.8361 125.957
                20.499 121.064 20.499H114.36Z"
                fill="#A7CB47"/>
            </svg>
          </a>
        </div>
        <div class="content-nav menu">
          <ul class="menu-desktop">
            <li v-for="item in MenuData" :key="item.id">
              <button
                v-if="hasChildren(item)"
                type="button"
                @click.stop="toggleMenu(item.id)"
                class="nav-trigger-btn"
                :class="[
                      colorClasses,
                      { 'active': activeMenu === item.id }
                        ]">
                {{ item.label }}
              </button>
              <a v-else :href="item.link"
                 :class="colorClasses">{{ item.label }}</a>
            </li>
          </ul>
          <div class="content-search search">
            <button @click.stop="toggleMenu('search')" :class="{ 'active': activeMenu === 'search' }">
              <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none"
                   stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <circle cx="11" cy="11" r="8"/>
                <path d="m21 21-4.34-4.34"/>
              </svg>
            </button>
          </div>
          <div class="icon-menu-mobile" @click="toggleMenuMobile">
            <svg xmlns="http://www.w3.org/2000/svg" width="30" height="11" viewBox="0 0 30 11" fill="none">
              <rect width="30" height="1" fill="white"/>
              <rect y="5" width="30" height="1" fill="white"/>
              <rect y="10" width="30" height="1" fill="white"/>
            </svg>
          </div>
        </div>
      </div>
    </nav>
    <template v-for="item in MenuData" :key="'mega-' + item.id">
      <div
        v-if="hasChildren(item)"
        class="mega-menu"
        :class="{ 'active': activeMenu === item.id }">
        <div class="modalidades">
          <div class="div-button">
            <button class="close-modalidades" @click="activeMenu = null">
              <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
                <path
                  d="M0.30545 1.7803C0.20861 1.68346 0.131793 1.56849 0.079384 1.44196C0.0269748 1.31544 1.02037e-09
                  1.17982 0 1.04287C-1.02037e-09 0.905921 0.0269748 0.77031 0.079384 0.643783C0.131793 0.517255
                  0.20861 0.40229 0.30545 0.30545C0.40229 0.20861 0.517255 0.131793 0.643783 0.079384C0.77031
                  0.0269748 0.905921 -1.02037e-09 1.04287 0C1.17982 1.02037e-09 1.31544 0.0269748 1.44196
                  0.079384C1.56849 0.131793 1.68346 0.20861 1.7803 0.30545L12.5 11.0272L23.2197 0.30545C23.3165
                  0.20861 23.4315 0.131793 23.558 0.079384C23.6846 0.0269748 23.8202 0 23.9571 0C24.0941 0 24.2297
                  0.0269748 24.3562 0.079384C24.4827 0.131793 24.5977 0.20861 24.6946 0.30545C24.7914 0.40229 24.8682
                  0.517255 24.9206 0.643783C24.973 0.77031 25 0.905921 25 1.04287C25 1.17982 24.973 1.31544 24.9206
                  1.44196C24.8682 1.56849 24.7914 1.68346 24.6946 1.7803L13.9728 12.5L24.6946 23.2197C24.7914 23.3165
                  24.8682 23.4315 24.9206 23.558C24.973 23.6846 25 23.8202 25 23.9571C25 24.0941 24.973 24.2297
                  24.9206 24.3562C24.8682 24.4827 24.7914 24.5977 24.6946 24.6946C24.5977 24.7914 24.4827 24.8682
                  24.3562 24.9206C24.2297 24.973 24.0941 25 23.9571 25C23.8202 25 23.6846 24.973 23.558
                  24.9206C23.4315 24.8682 23.3165 24.7914 23.2197 24.6946L12.5 13.9728L1.7803 24.6946C1.68346
                  24.7914 1.56849 24.8682 1.44196 24.9206C1.31544 24.973 1.17982 25 1.04287 25C0.905921 25 0.77031
                  24.973 0.643783 24.9206C0.517255 24.8682 0.40229 24.7914 0.30545 24.6946C0.20861 24.5977 0.131793
                  24.4827 0.079384 24.3562C0.0269748 24.2297 0 24.0941 0 23.9571C0 23.8202 0.0269748 23.6846
                  0.079384 23.558C0.131793 23.4315 0.20861 23.3165 0.30545 23.2197L11.0272 12.5L0.30545 1.7803Z"
                  fill="white"/>
              </svg>
            </button>
          </div>
          <div class="menu-produtos">
            <div class="rural-mobilidade" v-if="model.label === 'Mobilidade Rural'">
              <div class="mega-menu-grid">
                <div class="items">
                  <template v-if="isProductMenu(item)">
                    <div class="mega-column-full-width">
                      <slider-menu-products :related="model.paginatedProducts?.products"
                                            :perView="6" />
                    </div>
                  </template>
                  <template v-else>
                    <div v-for="col in item.childs" :key="col.id" class="mega-column">
                      <a :href="col.link" :class="col.class">
                        <figure class="centered-figure">
                          <img :src="col.featureImage?.fullUrl" :alt="col.label">
                          <figcaption>{{ col.alternative || col.label }}</figcaption>
                        </figure>
                      </a>
                    </div>
                  </template>
                </div>
              </div>
            </div>
            <div class="urbana-mobilidade" v-else-if="model.label === 'Mobilidade Urbana'">
              <div class="mega-menu-grid">
                <div class="items">
                  <template v-if="isProductMenu(item)">
                    <div class="mega-column-full-width">
                      <slider-menu-urbano :model="model.paginatedProducts?.products"
                                          :perView="4"
                                          :color="colorClasses"/>
                    </div>
                  </template>
                  <template v-else>
                    <div v-for="col in item.childs" :key="col.id" class="mega-column">
                      <a :href="col.link" :class="col.class">
                        <figure class="centered-figure">
                          <img :src="col.featureImage?.fullUrl" :alt="col.label">
                          <figcaption>{{ col.alternative || col.label }}</figcaption>
                        </figure>
                      </a>
                    </div>
                  </template>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
    <div class="mega-menu search-menu" :class="{ 'active': activeMenu === 'search' }">
      <div class="search-container">
        <div class="search-input-wrapper">
          <button class="close-search" @click="activeMenu = null">
            <svg xmlns="http://www.w3.org/2000/svg" width="25" height="25" viewBox="0 0 25 25" fill="none">
              <path
                d="M0.30545 1.7803C0.20861 1.68346 0.131793 1.56849 0.079384 1.44196C0.0269748 1.31544 1.02037e-09
                1.17982 0 1.04287C-1.02037e-09 0.905921 0.0269748 0.77031 0.079384 0.643783C0.131793 0.517255
                0.20861 0.40229 0.30545 0.30545C0.40229 0.20861 0.517255 0.131793 0.643783 0.079384C0.77031 0.0269748
                0.905921 -1.02037e-09 1.04287 0C1.17982 1.02037e-09 1.31544 0.0269748 1.44196 0.079384C1.56849
                0.131793 1.68346 0.20861 1.7803 0.30545L12.5 11.0272L23.2197 0.30545C23.3165 0.20861 23.4315 0.131793
                23.558 0.079384C23.6846 0.0269748 23.8202 0 23.9571 0C24.0941 0 24.2297 0.0269748 24.3562
                0.079384C24.4827 0.131793 24.5977 0.20861 24.6946 0.30545C24.7914 0.40229 24.8682 0.517255 24.9206
                0.643783C24.973 0.77031 25 0.905921 25 1.04287C25 1.17982 24.973 1.31544 24.9206 1.44196C24.8682
                1.56849 24.7914 1.68346 24.6946 1.7803L13.9728 12.5L24.6946 23.2197C24.7914 23.3165 24.8682 23.4315
                24.9206 23.558C24.973 23.6846 25 23.8202 25 23.9571C25 24.0941 24.973 24.2297 24.9206 24.3562C24.8682
                24.4827 24.7914 24.5977 24.6946 24.6946C24.5977 24.7914 24.4827 24.8682 24.3562 24.9206C24.2297
                24.973 24.0941 25 23.9571 25C23.8202 25 23.6846 24.973 23.558 24.9206C23.4315 24.8682 23.3165
                24.7914 23.2197 24.6946L12.5 13.9728L1.7803 24.6946C1.68346 24.7914 1.56849 24.8682 1.44196
                24.9206C1.31544 24.973 1.17982 25 1.04287 25C0.905921 25 0.77031 24.973 0.643783 24.9206C0.517255
                24.8682 0.40229 24.7914 0.30545 24.6946C0.20861 24.5977 0.131793 24.4827 0.079384 24.3562C0.0269748
                24.2297 0 24.0941 0 23.9571C0 23.8202 0.0269748 23.6846 0.079384 23.558C0.131793 23.4315 0.20861
                23.3165 0.30545 23.2197L11.0272 12.5L0.30545 1.7803Z"
                fill="white"/>
            </svg>
          </button>
          <form :action="searchAction" method="GET" class="search-input">
            <input
              type="text"
              v-model="query"
              ref="inputSearchFocus"
              @keyup.enter="handleSearch"
              :placeholder="searchPlaceholder">
          </form>
        </div>
      </div>
    </div>
  </header>
  <div v-if="isMenuOpen" class="menu-overlay" @click="closeMenu"></div>
  <div class="menu-mobile" :class="{ 'open': isMenuOpen }">
    <menu-mobile :model="model"
    :items="MenuData"/>
  </div>
</template>

<style scoped>

.menu-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.5);
  z-index: 998;
}

.menu-mobile {
  position: fixed;
  right: -100%;
  top: 0;
  width: 250px;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  transition: right 0.3s ease;
  z-index: 999;
}

.menu-mobile.open {
  right: 0;
}

</style>
